import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Save, Bell, CheckCircle,
  RotateCcw, AlertTriangle, ChevronDown, ChevronRight,
  MonitorSmartphone, Send, Plus, X,
} from 'lucide-react';

// ─── Toggle ───────────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none
        ${checked ? 'bg-brand-700' : 'bg-gray-200'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform
          ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

// ─── Hälytysten osoitelista (#35) ─────────────────────────────────────────────
// Backend tallentaa listan pilkulla eroteltuna notification_email-kenttään.
// Tyhjä lista = hälytykset kaikille asiakkaan käyttäjille.

function splitEmails(value) {
  return String(value ?? '').split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);
}

function EmailListInput({ emails, onChange, disabled }) {
  const { t } = useTranslation();
  const rows = emails.length ? emails : [''];
  const hasAny = emails.some(e => e.trim());

  function setAt(i, v) { onChange(rows.map((e, j) => (j === i ? v : e))); }
  function removeAt(i) { onChange(rows.filter((_, j) => j !== i)); }
  function add() { onChange([...rows, '']); }

  return (
    <div className="flex-1 min-w-0 space-y-2">
      {rows.map((e, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="email"
            placeholder={t('settings.notifications.emailPlaceholder')}
            value={e}
            onChange={ev => setAt(i, ev.target.value)}
            disabled={disabled}
            className="flex-1 min-w-0 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-brand-500 disabled:opacity-50 disabled:bg-gray-50"
          />
          {(rows.length > 1 || e) && (
            <button
              type="button"
              onClick={() => removeAt(i)}
              disabled={disabled}
              title={t('settings.notifications.removeEmail')}
              aria-label={t('settings.notifications.removeEmail')}
              className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
            >
              <X size={14} />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1 text-xs text-brand-700 hover:text-brand-800"
        >
          <Plus size={12} />{t('settings.notifications.addEmail')}
        </button>
      )}
      <p className="text-xs text-gray-400">
        {hasAny ? t('settings.notifications.emailHintList') : t('settings.notifications.emailHintEmpty')}
      </p>
    </div>
  );
}

// ─── Osio A: Ilmoitusasetukset (PoC: verkkovirta) ────────────────────────────

function NotificationSettingsSection({ tenantId }) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  // Superadmin voi muokata vain kun tenantti on eksplisiittisesti valittu (tenantId
  // tulee /settings/:id-reitiltä Asiakaskortin kautta, ks. PoC 7) — estää vahingossa
  // tapahtuvan muokkauksen Superadminin omaan, epäselvään tenanttiin bare /settings-polulla.
  const canEdit = profile?.role === 'admin' || (profile?.role === 'superadmin' && !!tenantId);
  const query = tenantId ? `?tenant_id=${tenantId}` : '';

  const SENSOR_ROWS = [
    { key: 'power',   label: t('sensors.power')   },
  ];

  const defaultSettings = {
    power:   { sensor_type: 'power',   show_on_dashboard: true, email_enabled: true, notification_emails: [], repeat_interval_hours: 24 },
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/api/alerts/tenant-settings${query}`)
      .then(r => {
        if (Array.isArray(r.data)) {
          const map = {};
          for (const row of r.data) map[row.sensor_type] = { ...row, notification_emails: splitEmails(row.notification_email) };
          setSettings(prev => ({ ...prev, ...map }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [query]);

  // Testihälytys käyttää tallennettuja asetuksia, joten se estetään tallentamattomien
  // muutosten ajaksi (#33)
  const [dirty, setDirty] = useState(false);

  function updateSetting(key, field, value) {
    setSettings(s => ({ ...s, [key]: { ...s[key], [field]: value } }));
    setDirty(true);
  }

  async function save() {
    // Toistoväli: 0 = ei toistoa, muuten kokonaisluku 1–24 h (tallennetaan sellaisenaan)
    const invalidRepeat = SENSOR_ROWS.some(row => {
      const h = settings[row.key]?.repeat_interval_hours ?? 24;
      return !(Number.isInteger(h) && h >= 0 && h <= 24);
    });
    if (invalidRepeat) { setError(t('settings.notifications.repeatInvalid')); return; }

    setSaving(true); setSaved(false); setError('');
    try {
      const payload = SENSOR_ROWS.map(row => ({
        sensor_type: row.key,
        severity: 'critical',
        show_on_dashboard: settings[row.key]?.show_on_dashboard ?? true,
        email_enabled: settings[row.key]?.email_enabled ?? true,
        notification_email: splitEmails((settings[row.key]?.notification_emails ?? []).join(',')),
        repeat_interval_hours: settings[row.key]?.repeat_interval_hours ?? 24,
      }));
      await api.patch(`/api/alerts/tenant-settings${query}`, payload);
      setSaved(true);
      setDirty(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || t('settings.notifications.failed'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">{t('settings.notifications.loading')}</div>;

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        {t('settings.notifications.description')}
        {!canEdit && <span className="ml-1 text-gray-400">{t('settings.notifications.readOnly')}</span>}
      </p>

      <div className="space-y-4">
        {SENSOR_ROWS.map(row => {
          const s = settings[row.key] || {};
          const emailOn = s.email_enabled ?? true;
          const repeatHours = s.repeat_interval_hours ?? 24;
          const repeatOn = repeatHours !== 0;
          return (
            <div key={row.key} className="py-3 border-b border-gray-100 last:border-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="font-medium text-gray-800 w-32 shrink-0">{row.label}</span>
              <div className="flex items-center gap-2 shrink-0">
                <Toggle
                  checked={s.show_on_dashboard ?? true}
                  onChange={v => updateSetting(row.key, 'show_on_dashboard', v)}
                  disabled={!canEdit}
                />
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <MonitorSmartphone size={12} />{t('settings.notifications.banner')}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Toggle
                  checked={s.email_enabled ?? true}
                  onChange={v => updateSetting(row.key, 'email_enabled', v)}
                  disabled={!canEdit}
                />
                <span className="text-xs text-gray-500">{t('settings.notifications.email')}</span>
              </div>
              <EmailListInput
                emails={s.notification_emails ?? []}
                onChange={list => updateSetting(row.key, 'notification_emails', list)}
                disabled={!canEdit || !emailOn}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3 sm:ml-32 sm:pl-3">
              <Toggle
                checked={repeatOn}
                onChange={v => updateSetting(row.key, 'repeat_interval_hours', v ? 24 : 0)}
                disabled={!canEdit || !emailOn}
              />
              <span className="text-xs text-gray-500">{t('settings.notifications.repeat')}</span>
              {repeatOn && (
                <>
                  <input
                    type="number"
                    min={1}
                    max={24}
                    step={1}
                    value={repeatHours}
                    onChange={e => updateSetting(row.key, 'repeat_interval_hours',
                      e.target.value === '' ? '' : Number(e.target.value))}
                    disabled={!canEdit || !emailOn}
                    className="w-16 text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-brand-500 disabled:opacity-50 disabled:bg-gray-50"
                  />
                  <span className="text-xs text-gray-500">{t('settings.notifications.repeatUnit')}</span>
                </>
              )}
              <span className="text-xs text-gray-400 w-full">{t('settings.notifications.repeatHint')}</span>
            </div>
            </div>
          );
        })}
      </div>

      {error && <p className="text-xs text-red-500 mt-3">{error}</p>}

      {canEdit && (
        <button
          onClick={save}
          disabled={saving}
          className="mt-5 flex items-center gap-1.5 bg-brand-700 text-white text-sm px-4 py-2 rounded-lg hover:bg-brand-800 disabled:opacity-60 transition-colors"
        >
          {saving ? <><RotateCcw size={13} className="animate-spin" />{t('settings.notifications.saving')}</>
           : saved ? <><CheckCircle size={13} />{t('settings.notifications.saved')}</>
           : <><Save size={13} />{t('settings.notifications.save')}</>}
        </button>
      )}

      {canEdit && <TestAlertBox query={query} dirty={dirty} />}
    </div>
  );
}

// ─── Testihälytys (#33, 30.9.2026) ────────────────────────────────────────────
// Lähettää testisähköpostin samoille vastaanottajille ja samaa reittiä kuin oikea
// hälytys ja näyttää tuloksen. Ei hälytysriviä.

function TestAlertBox({ query, dirty }) {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { status, recipients, error } | { status: 'error', message }
  // #36: testin vastaanottajat valitaan oikean hälytyksen vastaanottajista, ei oletuksena ketään
  const [candidates, setCandidates] = useState(null); // { email_enabled, recipients } | null
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (dirty) return;
    api.get(`/api/alerts/test-notification/recipients${query}`)
      .then(r => {
        setCandidates(r.data);
        setSelected(sel => sel.filter(e => r.data.recipients.includes(e)));
      })
      .catch(() => setCandidates(null));
  }, [query, dirty]);

  function toggle(email) {
    setSelected(sel => (sel.includes(email) ? sel.filter(e => e !== email) : [...sel, email]));
  }

  const options = candidates?.recipients ?? [];
  const emailOff = candidates && !candidates.email_enabled;
  const canSend = !sending && !dirty && (emailOff || selected.length > 0);

  async function sendTest() {
    setSending(true); setResult(null);
    try {
      const { data } = await api.post(`/api/alerts/test-notification${query}`, { recipients: selected });
      setResult(data);
    } catch (err) {
      setResult({ status: 'error', message: err.response?.data?.error || t('settings.testAlert.requestFailed') });
    } finally {
      setSending(false);
    }
  }

  const recipients = result?.recipients?.join(', ');

  return (
    <div className="mt-6 pt-5 border-t border-gray-100">
      <h3 className="text-sm font-semibold text-gray-800">{t('settings.testAlert.title')}</h3>
      <p className="text-sm text-gray-600 mt-1">{t('settings.testAlert.description')}</p>
      {!dirty && !emailOff && options.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-600">{t('settings.testAlert.chooseRecipients')}</span>
            {options.length > 1 && (
              <button
                type="button"
                onClick={() => setSelected(selected.length === options.length ? [] : options)}
                className="text-xs text-brand-700 hover:text-brand-800"
              >
                {selected.length === options.length ? t('settings.testAlert.selectNone') : t('settings.testAlert.selectAll')}
              </button>
            )}
          </div>
          <div className="mt-1.5 space-y-1">
            {options.map(email => (
              <label key={email} className="flex items-center gap-2 text-sm text-gray-700 break-all cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(email)}
                  onChange={() => toggle(email)}
                  className="h-4 w-4 shrink-0 accent-brand-700"
                />
                {email}
              </label>
            ))}
          </div>
        </div>
      )}
      <button
        onClick={sendTest}
        disabled={!canSend}
        className="mt-3 flex items-center gap-1.5 border border-brand-700 text-brand-700 text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {sending ? <RotateCcw size={14} className="animate-spin" /> : <Send size={14} />}
        {sending ? t('settings.testAlert.sending') : t('settings.testAlert.send')}
      </button>
      {dirty && <p className="text-xs text-gray-500 mt-2">{t('settings.testAlert.saveFirst')}</p>}
      {!dirty && candidates && !emailOff && options.length === 0 && (
        <p className="text-xs text-red-600 mt-2">{t('settings.testAlert.noRecipients')}</p>
      )}
      {!dirty && !emailOff && options.length > 0 && selected.length === 0 && (
        <p className="text-xs text-gray-500 mt-2">{t('settings.testAlert.selectFirst')}</p>
      )}

      {result && (
        <div className={`mt-3 rounded-lg border px-3 py-2.5 text-sm ${
          result.status === 'sent' ? 'bg-green-50 border-green-200 text-green-800'
          : result.status === 'disabled' ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
          : 'bg-red-50 border-red-200 text-red-800'}`}
        >
          {result.status === 'sent' && (
            <>
              <p className="font-medium flex items-center gap-1.5"><CheckCircle size={15} />{t('settings.testAlert.sent', { recipients })}</p>
              <p className="mt-1">{t('settings.testAlert.checkInbox')}</p>
            </>
          )}
          {result.status === 'disabled' && <p>{t('settings.testAlert.disabled')}</p>}
          {result.status === 'failed' && (
            <>
              <p className="font-medium flex items-center gap-1.5">
                <AlertTriangle size={15} />
                {result.recipients?.length ? t('settings.testAlert.failed', { recipients }) : t('settings.testAlert.noRecipients')}
              </p>
              {result.error && <p className="mt-1 text-xs break-words">{result.error}</p>}
            </>
          )}
          {result.status === 'error' && <p>{result.message}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Osio B: Hälytysrajat ────────────────────────────────────────────────────

// Epic 2.8: tämä säädin ohjaa laitteen tavoitettavuuden (heartbeat-poissaolon,
// sensor_type='connection') hälytyskynnystä — avain pysyy 'power':na
// tenant_alert_thresholds/shelter_alert_thresholds-tauluissa (ei migraatiota),
// vain näkyvä teksti on päivitetty vastaamaan uutta merkitystä.
const THRESHOLD_SENSORS = [
  { key: 'power',   labelKey: 'settings.thresholds.reachability', unit: 'min', min: 5, max: 60, step: 1, defaultVal: 15 },
];

function ThresholdInput({ sensor, value, onChange, disabled }) {
  const { t } = useTranslation();
  const meta = THRESHOLD_SENSORS.find(s => s.key === sensor) || {};
  const val = value !== undefined && value !== null ? Number(value) : meta.defaultVal;

  return (
    <div>
      <label className="text-xs font-medium text-gray-600 block mb-1">
        {t(meta.labelKey)} ({meta.unit})
      </label>
      <input
        type="range"
        min={meta.min}
        max={meta.max}
        step={meta.step}
        value={val}
        onChange={e => onChange(sensor, Number(e.target.value))}
        disabled={disabled}
        className="w-full accent-brand-600 disabled:opacity-50"
      />
      <div className="flex justify-between items-center mt-1">
        <span className="text-xs text-gray-400">{meta.min}</span>
        <span className="text-sm font-semibold text-gray-900">{val} {meta.unit}</span>
        <span className="text-xs text-gray-400">{meta.max}</span>
      </div>
    </div>
  );
}

function OverrideDialog({ onConfirm, onCancel }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-1">{t('settings.thresholds.override.title')}</p>
            <p className="text-sm text-gray-600">{t('settings.thresholds.override.message')}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => onConfirm(false)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {t('settings.thresholds.override.keep')}
          </button>
          <button
            onClick={() => onConfirm(true)}
            className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            {t('settings.thresholds.override.overrideAll')}
          </button>
        </div>
      </div>
    </div>
  );
}

function AlertThresholdsSection() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const canEdit = profile?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [shelters, setShelters] = useState([]);
  const [selectedShelter, setSelectedShelter] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [shelterEditValues, setShelterEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [shelterSaving, setShelterSaving] = useState(false);
  const [shelterSaved, setShelterSaved] = useState(false);

  const loadThresholds = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/alerts/thresholds');
      const tMap = {};
      (data.tenant_thresholds || []).forEach(th => { tMap[th.sensor_type] = th.critical_value; });
      setEditValues({ ...tMap });
      setShelters(data.shelters || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadThresholds(); }, [loadThresholds]);

  function handleTenantChange(sensorType, value) {
    setEditValues(v => ({ ...v, [sensorType]: value }));
  }

  async function saveTenantThresholds(overrideCustom) {
    setShowOverrideDialog(false);
    setSaving(true); setSaved(false);
    try {
      for (const sensor of THRESHOLD_SENSORS) {
        await api.patch('/api/alerts/thresholds/tenant', {
          sensor_type: sensor.key,
          critical_value: editValues[sensor.key] ?? sensor.defaultVal,
          override_custom: overrideCustom,
        });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await loadThresholds();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  }

  function handleSaveClick() {
    const hasCustom = shelters.some(s => s.has_custom);
    if (hasCustom) {
      setShowOverrideDialog(true);
    } else {
      saveTenantThresholds(false);
    }
  }

  function openShelterEdit(shelter) {
    const vals = {};
    (shelter.thresholds || []).forEach(th => { vals[th.sensor_type] = th.critical_value; });
    setShelterEditValues(vals);
    setSelectedShelter(shelter);
    setShelterSaved(false);
  }

  function handleShelterChange(sensorType, value) {
    setShelterEditValues(v => ({ ...v, [sensorType]: value }));
  }

  async function saveShelterThresholds() {
    if (!selectedShelter) return;
    setShelterSaving(true); setShelterSaved(false);
    try {
      for (const sensor of THRESHOLD_SENSORS) {
        await api.patch(`/api/alerts/thresholds/shelter/${selectedShelter.id}`, {
          sensor_type: sensor.key,
          critical_value: shelterEditValues[sensor.key] ?? sensor.defaultVal,
        });
      }
      setShelterSaved(true);
      setTimeout(() => setShelterSaved(false), 3000);
      await loadThresholds();
      setSelectedShelter(prev => {
        const updated = shelters.find(s => s.id === prev?.id);
        return updated || prev;
      });
    } catch {
      // ignore
    } finally {
      setShelterSaving(false);
    }
  }

  async function resetAllShelterToDefault(shelterId) {
    try {
      for (const sensor of THRESHOLD_SENSORS) {
        await api.delete(`/api/alerts/thresholds/shelter/${shelterId}/${sensor.key}`).catch(() => {});
      }
      setSelectedShelter(null);
      await loadThresholds();
    } catch {
      // ignore
    }
  }

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">{t('settings.thresholds.loading')}</div>;

  return (
    <div className="space-y-6">
      {showOverrideDialog && (
        <OverrideDialog
          onConfirm={overrideCustom => saveTenantThresholds(overrideCustom)}
          onCancel={() => setShowOverrideDialog(false)}
        />
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">{t('settings.thresholds.generic.title')}</h3>
        <p className="text-xs text-gray-500 mb-4">
          {t('settings.thresholds.generic.description')}
          {!canEdit && <span className="ml-1 text-gray-400">{t('settings.thresholds.generic.readOnly')}</span>}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {THRESHOLD_SENSORS.map(sensor => (
            <ThresholdInput
              key={sensor.key}
              sensor={sensor.key}
              value={editValues[sensor.key]}
              onChange={handleTenantChange}
              disabled={!canEdit}
            />
          ))}
        </div>

        {canEdit && (
          <button
            onClick={handleSaveClick}
            disabled={saving}
            className="mt-5 flex items-center gap-1.5 bg-brand-700 text-white text-sm px-4 py-2 rounded-lg hover:bg-brand-800 disabled:opacity-60 transition-colors"
          >
            {saving ? <><RotateCcw size={13} className="animate-spin" />{t('settings.thresholds.generic.saving')}</>
             : saved ? <><CheckCircle size={13} />{t('settings.thresholds.generic.saved')}</>
             : <><Save size={13} />{t('settings.thresholds.generic.saveAll')}</>}
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">{t('settings.thresholds.perShelter.title')}</h3>
        <p className="text-xs text-gray-500 mb-4">{t('settings.thresholds.perShelter.description')}</p>

        <div className="divide-y divide-gray-100">
          {shelters.length === 0 && (
            <p className="text-sm text-gray-400 py-4 text-center">{t('settings.thresholds.perShelter.noShelters')}</p>
          )}
          {shelters.map(shelter => (
            <div key={shelter.id}>
              <button
                onClick={() => selectedShelter?.id === shelter.id ? setSelectedShelter(null) : openShelterEdit(shelter)}
                className="w-full flex items-center gap-3 py-3 text-left hover:bg-gray-50 transition-colors px-1 rounded"
              >
                {selectedShelter?.id === shelter.id
                  ? <ChevronDown size={14} className="text-gray-400 shrink-0" />
                  : <ChevronRight size={14} className="text-gray-400 shrink-0" />
                }
                <span className="text-sm font-medium text-gray-800 flex-1">{shelter.name}</span>
                {shelter.has_custom && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium shrink-0">
                    {t('settings.thresholds.perShelter.customBadge')}
                  </span>
                )}
              </button>

              {selectedShelter?.id === shelter.id && (
                <div className="pb-4 pl-5 pr-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    {THRESHOLD_SENSORS.map(sensor => (
                      <ThresholdInput
                        key={sensor.key}
                        sensor={sensor.key}
                        value={shelterEditValues[sensor.key]}
                        onChange={handleShelterChange}
                        disabled={!canEdit}
                      />
                    ))}
                  </div>

                  {canEdit && (
                    <div className="flex gap-3 flex-wrap">
                      <button
                        onClick={saveShelterThresholds}
                        disabled={shelterSaving}
                        className="flex items-center gap-1.5 bg-brand-700 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-brand-800 disabled:opacity-60 transition-colors"
                      >
                        {shelterSaving ? <><RotateCcw size={13} className="animate-spin" />{t('settings.thresholds.perShelter.saving')}</>
                         : shelterSaved ? <><CheckCircle size={13} />{t('settings.thresholds.perShelter.saved')}</>
                         : <><Save size={13} />{t('settings.thresholds.perShelter.save')}</>}
                      </button>
                      {shelter.has_custom && (
                        <button
                          onClick={() => resetAllShelterToDefault(shelter.id)}
                          className="flex items-center gap-1.5 border border-gray-300 text-gray-600 text-sm px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <RotateCcw size={13} />{t('settings.thresholds.perShelter.reset')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SettingsPage ─────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { id: tenantId } = useParams();
  const [tab, setTab] = useState('notifications');

  const isAdminOrSuperadmin = profile?.role === 'admin' || profile?.role === 'superadmin';

  const TABS = [
    { key: 'notifications', label: t('settings.tabs.notifications'), icon: Bell },
    { key: 'thresholds',    label: t('settings.tabs.thresholds'),    icon: AlertTriangle },
  ];

  return (
    <div>
      <h1 className="text-xl font-bold text-gray-900 mb-4">{t('settings.title')}</h1>

      <div className="flex items-center gap-1 border-b border-gray-200 mb-5">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
              ${tab === key
                ? 'border-brand-700 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'notifications' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <NotificationSettingsSection tenantId={tenantId} />
        </div>
      )}

      {tab === 'thresholds' && isAdminOrSuperadmin && (
        <AlertThresholdsSection />
      )}

      {tab === 'thresholds' && !isAdminOrSuperadmin && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
          {t('settings.thresholds.adminOnly')}
        </div>
      )}

    </div>
  );
}
